"use client";

import styles from "@/styles/global/navbar.module.scss";
import { useState, useEffect, useRef } from "react";
import Link from "next/link";
import Image from "next/image";
import ProductDetailSearchBar from "@/components/product-detail/ProductDetailSearchBar";
import { Badge, Popup } from "react-vant";
import { Arrow, Cross } from "@react-vant/icons";
import { removeCookie } from "@/utils/cookie";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { openCategoryPopup } from "@/store/slices/modalSlice";
import { fetchCustomerInfo } from "@/store/slices/userSlice";
import { getCartList } from "@/store/slices/cartListSlice";
import { usePathname } from "next/navigation";

interface menuType {
  name: string;
  link: string;
  icon: string;
  badgeNum: number;
}

interface NavbarProps {
  hideCategoryIcon?: boolean;
  hideAvatarIcon?: boolean;
}

export default function NavBar({
  hideCategoryIcon = false,
  hideAvatarIcon = false,
}: NavbarProps) {
  const pathname = usePathname();
  const showCategoryIcon =
    !pathname.includes("/checkout") &&
    !pathname.includes("/payment-result/") &&
    !pathname.includes("/account/my-addresses") &&
    !pathname.includes("/account/create-address") &&
    !pathname.includes("/account/edit-address") &&
    !pathname.includes("/online-") &&
    !pathname.includes("/view-cart");

  const showWishlistIcon =
    !pathname.includes("/checkout") &&
    !pathname.includes("/payment-result/") &&
    !pathname.includes("/account/my-addresses") &&
    !pathname.includes("/account/create-address") &&
    !pathname.includes("/account/edit-address") &&
    !pathname.includes("/online-") &&
    !pathname.includes("/view-cart");

  const showSearchIcon =
    (pathname.includes("/online-") && pathname.includes(".html")) ||
    pathname.includes("/view-cart");
  const showCartIcon =
    !pathname.includes("/checkout") &&
    !pathname.includes("/payment-result/") &&
    !pathname.includes("/account/my-addresses") &&
    !pathname.includes("/account/create-address") &&
    !pathname.includes("/account/edit-address");
  const showAvatarIcon = !pathname.includes("/checkout/express");
  const dispatch = useAppDispatch();
  const navbarRef = useRef<HTMLDivElement>(null);
  const [userPopupVisible, setUserPopupVisible] = useState(false);
  const [searchBarVisible, setSearchBarVisible] = useState(false);
  const [cartCount, setCartCount] = useState(0);

  // 从Redux store获取用户状态和购物车状态
  const { customerInfo, hasLogin } = useAppSelector((state) => state.user);
  const { data: cartData } = useAppSelector((state) => state.cartList);

  // 组件挂载时获取用户信息
  useEffect(() => {
    dispatch(fetchCustomerInfo());
    dispatch(getCartList());
  }, [dispatch]);

  // 当购物车数据更新时，更新UI显示的数量
  useEffect(() => {
    if (cartData && cartData.count !== undefined) {
      setCartCount(cartData.count);
    }
  }, [cartData]);

  // 添加dataLayer推送 - 使用定时器检查dataLayer是否已加载
  useEffect(() => {
    // 只有在首页时才执行dataLayer推送
    if (pathname !== "/") {
      return;
    }

    // 定义要推送的数据
    const dataLayerPayload = {
      event: "homePage",
      remarketing_params: {
        ecomm_prodid: "",
        ecomm_pagetype: "home",
        ecomm_totalvalue: "",
      },
      params: {
        userId: customerInfo.customerID || null,
        categoryId: null,
      },
    };

    // 尝试立即推送
    if (typeof window !== "undefined" && window.dataLayer) {
      window.dataLayer.push(dataLayerPayload);
      return; // 如果dataLayer已存在，直接推送并返回
    }

    // 如果dataLayer不存在，设置定时器定期检查
    const checkInterval = 800; // 每200毫秒检查一次
    const maxAttempts = 6; // 最多尝试50次，即10秒
    let attempts = 0;

    const intervalId = setInterval(() => {
      attempts++;

      if (typeof window !== "undefined" && window.dataLayer) {
        // dataLayer已加载，执行推送
        window.dataLayer.push(dataLayerPayload);
        clearInterval(intervalId); // 清除定时器
      } else if (attempts >= maxAttempts) {
        // 达到最大尝试次数，放弃并清除定时器
        clearInterval(intervalId);
        console.log("GTM dataLayer not available after maximum attempts");
      }
    }, checkInterval);

    // 组件卸载时清除定时器
    return () => clearInterval(intervalId);
  }, [customerInfo.customerID, pathname]); // 添加customerInfo.customerID和pathname作为依赖项

  const menuList: menuType[] = [
    {
      name: "Home",
      link: "/",
      icon: `${process.env.IMAGE_DOMAIN}/home/user-menu/home.svg`,
      badgeNum: 0,
    },
    {
      name: "Orders",
      link: hasLogin ? "/my-account/order/0" : "/login?redirect=%2Fmy-account%2Forder%2F0",
      icon: `${process.env.IMAGE_DOMAIN}/home/user-menu/order.svg`,
      badgeNum: 0,
    },
    {
      name: "Address Manager",
      link: hasLogin ? "/account/my-addresses" : "/login?redirect=%2Faccount%2Fmy-addresses",
      icon: `${process.env.IMAGE_DOMAIN}/home/user-menu/address.svg`,
      badgeNum: 0,
    },
    {
      name: "Shopping Cart",
      link: "/view-cart",
      icon: `${process.env.IMAGE_DOMAIN}/home/user-menu/cart.svg`,
      badgeNum: cartCount || 0,
    },
    {
      name: "My Wishlist",
      link: hasLogin ? "/my-account/wishlist" : "/login?redirect=%2Fmy-account%2Fwishlist",
      icon: `${process.env.IMAGE_DOMAIN}/svg/icon-wishlist-bule-full.svg`,
      badgeNum: 0,
    },
    {
      name: "Coupon",
      link: hasLogin ? "/my-account/credits" : "/login?redirect=%2Fmy-account%2Fcredits",
      icon: `${process.env.IMAGE_DOMAIN}/home/user-menu/credits.svg`,
      badgeNum: 0,
    },
    {
      name: hasLogin ? "Sign Out" : "Sign in / Join Free",
      link: hasLogin ? "/account/logout" : "/login?redirect=%2F",
      icon: `${process.env.IMAGE_DOMAIN}/home/user-menu/loginDetails.svg`,
      badgeNum: 0,
    },
  ];

  const handleViewDetail = (menu: menuType) => {
    if (!menu.link) return;
    setUserPopupVisible(false);
    if (menu.link == "/account/logout") {
      localStorage.removeItem("tokenExpired");
      localStorage.removeItem("userToken");
      localStorage.removeItem("userEmail");
      removeCookie("PHPSESSID");
      removeCookie("userCustomerId");

      setTimeout(() => {
        // 如果当前路径包含checkout，跳转到购物车页面；否则刷新页面
        if (pathname.includes("/checkout")) {
          window.location.href = "/view-cart";
        } else if (pathname.includes("/my-account/")) {
          window.location.href = "/";
        } else {
          window.location.reload();
        }
      }, 200);
      return;
    }
    window.location.href = menu.link;
  };

  return (
    <div className={styles["comp-navbar"]} ref={navbarRef}>
      <div className={styles["navbar-content"]}>
        <div className={styles["navbar-left"]}>
          {showCategoryIcon && !hideCategoryIcon ? (
            <Image
              className={styles["icon-menu"]}
              src={`${process.env.IMAGE_DOMAIN}/svg/icon-menu.svg`}
              alt={"icon-menu"}
              onClick={() => dispatch(openCategoryPopup())}
              priority={true}
              width={27}
              height={27}
            />
          ) : null}

          <Link href={"/"}>
            <Image
              className={styles["icon-logo"]}
              src={`${process.env.IMAGE_DOMAIN}/svg/bestdeals-mob.svg`}
              alt={"menu-icon"}
              priority={true}
              width={115}
              height={33}
            />
          </Link>
        </div>

        <div className={styles["navbar-right"]}>
          {showWishlistIcon ? (
            <Image
              className={styles["icon-wishlist"]}
              src={`${
                hasLogin
                  ? `${process.env.IMAGE_DOMAIN}/svg/icon-wishlist-full-nav.svg`
                  : `${process.env.IMAGE_DOMAIN}/svg/icon-wishlist-nav.svg`
              }`}
              onClick={() =>
                hasLogin
                  ? (window.location.href = "/my-account/wishlist")
                  : (window.location.href = "/account/login?return=%2Fmy-account%2Fwishlist")
              }
              alt={"icon-wishlist"}
              priority={true}
              width={27}
              height={27}
            />
          ) : null}

          {showSearchIcon ? (
            <Image
              className={`${styles["icon-search-nav"]}`}
              src={`${process.env.IMAGE_DOMAIN}/svg/icon-search-nav.svg`}
              onClick={() => setSearchBarVisible(true)}
              alt={"icon-search"}
              width={28}
              height={28}
            />
          ) : null}

          {showCartIcon ? (
            <div
              className={`
              ${styles["shopping-cart-box"]}
              ${Number(cartCount) > 9 ? styles["icon-shopping-cart-mr7"] : ""}
              ${Number(cartCount) > 99 ? styles["icon-shopping-cart-mr8"] : ""}
              ${
                Number(cartCount) > 999 ? styles["icon-shopping-cart-mr10"] : ""
              }
            `}
            >
              <Badge
                content={cartCount || 0}
                color={"#FFFFFF"}
                showZero={false}
                offset={[6, 6]}
              >
                <Image
                  className={`${styles["icon-shopping-cart"]}`}
                  src={`${process.env.IMAGE_DOMAIN}/svg/navbar-cart.svg`}
                  style={
                    Number(cartCount) > 99 ? { marginRight: "12px" } : undefined
                  }
                  onClick={() => (window.location.href = "/view-cart")}
                  alt={"icon-shopping-cart"}
                  priority={true}
                  width={28}
                  height={28}
                />
              </Badge>
            </div>
          ) : null}

          {showAvatarIcon && !hideAvatarIcon ? (
            <Image
              className={styles["icon-avatar"]}
              src={`${
                hasLogin
                  ? `${process.env.IMAGE_DOMAIN}/svg/navbar-cart-full.svg`
                  : `${process.env.IMAGE_DOMAIN}/svg/navbar-cart-nav.svg`
              }`}
              onClick={() => setUserPopupVisible(true)}
              alt={"icon-avatar"}
              priority={true}
              width={27}
              height={27}
            />
          ) : null}

          <Popup
            teleport={() => navbarRef.current || document.body}
            visible={userPopupVisible}
            onClose={() => setUserPopupVisible(false)}
            position="right"
            closeOnPopstate={true}
            safeAreaInsetBottom={true}
            className={styles["user-popup-box"]}
            style={{ width: "100%", height: "100%", background: "transparent" }}
            overlayStyle={{ background: "rgba(0, 0, 0, 0.83)" }}
          >
            <div className={styles["user-popup-content"]}>
              {userPopupVisible ? (
                <div
                  className={styles["user-popup-close"]}
                  onClick={() => setUserPopupVisible(false)}
                >
                  <Cross color={"#FFFFFF"} fontSize={40} />
                </div>
              ) : null}
              <div
                className={styles["user-popup-title"]}
                style={
                  hasLogin
                    ? { background: "#436cb2" }
                    : { background: "#999999" }
                }
              >
                <Image
                  className={styles["icon-avatar"]}
                  src={`${process.env.IMAGE_DOMAIN}/svg/icon-avatar-full-nav.svg`}
                  alt={"icon-avatar"}
                  width={38}
                  height={38}
                />

                {hasLogin ? (
                  <div className={styles["user-name-box"]}>
                    <div className={styles["user-name"]}>
                      {customerInfo.firstName} {customerInfo.lastName} {`'s`}
                    </div>
                    <div className={styles["user-name-desc"]}>Account</div>
                  </div>
                ) : (
                  <div className={styles["to-login"]}>
                    <Link
                      href={"/login"}
                      onClick={() => setUserPopupVisible(false)}
                    >
                      Sign in / Join Free
                    </Link>
                  </div>
                )}
              </div>

              <div className={styles["user-popup-menu-list"]}>
                {menuList.map((menu: menuType, index: number) => (
                  <div
                    key={index}
                    className={styles["user-popup-menu-item"]}
                    onClick={() => handleViewDetail(menu)}
                  >
                    <Badge
                      content={menu.badgeNum}
                      color={"#4498b4"}
                      showZero={false}
                      offset={[3, 3]}
                    >
                      <Image
                        className={styles["icon-menu"]}
                        src={menu.icon}
                        alt={"icon-avatar"}
                        width={22}
                        height={22}
                      />
                    </Badge>

                    <div className={styles["menu-name"]}>{menu.name}</div>
                    <Arrow className={styles["arrow-icon"]} color="#DDDDDD" />
                  </div>
                ))}
              </div>
            </div>
          </Popup>
        </div>
      </div>

      {showSearchIcon && searchBarVisible && (
        <ProductDetailSearchBar onClose={() => setSearchBarVisible(false)} />
      )}
    </div>
  );
}
